import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import debounce from "lodash/debounce";
import api from "../../api";
import config from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 640,
    margin: "0 auto",
  },
  paper: { padding: theme.spacing(3) },
  field: { marginTop: theme.spacing(2), width: "100%" },
  button: { marginTop: theme.spacing(3) },
  result: { marginTop: theme.spacing(3) },
  hint: { marginTop: theme.spacing(1) },
}));

const PROD_ENVS = new Set(["production", "prod"]);

const TEST_PHONES = [
  { label: "Eng Test 1 — (737) 277-1757", value: "+17372771757" },
  { label: "Eng Test 2 — (737) 250-7391", value: "+17372507391" },
];

export default function SmsTestResetPage() {
  const classes = useStyles();
  const history = useHistory();
  const isProduction = PROD_ENVS.has(config.appEnv);

  const [partner, setPartner] = useState(null);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isProduction) history.replace("/");
  }, [isProduction, history]);

  const fetchPartners = useMemo(
    () =>
      debounce((search) => {
        setPartnerLoading(true);
        api
          .getPartners({ page: 1, perPage: 20, status: "active", search })
          .then((resp) => {
            setPartnerOptions(
              (resp.data.items || []).map((p) => ({ id: p.id, name: p.name })),
            );
          })
          .catch(() => setPartnerOptions([]))
          .finally(() => setPartnerLoading(false));
      }, 250),
    [],
  );

  useEffect(() => {
    fetchPartners(partnerSearch);
  }, [partnerSearch, fetchPartners]);

  if (isProduction) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const resp = await api.post("/admin/v1/dev_tools/reset_sms_contact", {
        partner_id: partner.id,
        phone: phone.trim(),
      });
      setResult(resp.data && resp.data.data);
    } catch (err) {
      const data = err.response && err.response.data;
      const msg =
        (data && data.error && data.error.message) ||
        (data && data.message) ||
        err.message ||
        "Reset failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Typography variant="h5" gutterBottom>
          SMS Test Reset
        </Typography>
        <Typography variant="body2" color="textSecondary">
          QA tool — soft-deletes a contact, its SMS-created leads, and the
          pending row in communication-service so the same phone number can
          replay an SMS flow from a clean state. Disabled in production.
        </Typography>

        <form onSubmit={submit}>
          <Autocomplete
            className={classes.field}
            options={partnerOptions}
            value={partner}
            onChange={(_, value) => setPartner(value)}
            onInputChange={(_, value) => setPartnerSearch(value)}
            getOptionLabel={(opt) => (opt ? `${opt.name} (#${opt.id})` : "")}
            getOptionSelected={(opt, val) => opt.id === val.id}
            filterOptions={(x) => x}
            loading={partnerLoading}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Partner (search by name or ID)"
                required
                name="partner-search"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password",
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {partnerLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <FormControl className={classes.field} component="fieldset" required disabled={loading}>
            <FormLabel component="legend">OpenPhone test number</FormLabel>
            <RadioGroup
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            >
              {TEST_PHONES.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Button
            className={classes.button}
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !partner || !phone}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Reset contact
          </Button>
        </form>

        {error && (
          <Alert className={classes.result} severity="error">
            {error}
          </Alert>
        )}

        {result && (
          <Alert className={classes.result} severity="success">
            <Typography variant="body2">Reset complete:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Contacts soft-deleted: {result.contactsDeleted}</li>
              <li>Contact methods soft-deleted: {result.contactMethodsDeleted}</li>
              <li>Leads soft-deleted: {result.leadsDeleted}</li>
              <li>Pending rows deleted: {result.pendingDeleted?.data?.deleted ?? "(comm-service unreachable)"}</li>
              <li>Conversations deleted: {result.conversationsDeleted?.data?.deleted ?? "(comm-service unreachable)"}</li>
            </ul>
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
